import React, { useState, useEffect } from "react";
import AnnouncementImages from "../components/announce/AnnouncementImages";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOpenKitchens, Kitchen } from "../services/kitchen.service";
import { getActiveNotices, Notice } from "../services/notice.service";
import { getDailyMenu, getLocalDateId } from "../services/admin.service";
import { CartItem } from "../types/index";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthProvider";

import CircleLogo from "../components/CircleLogo";
import { Card } from "../components/common/Card";
import { Spinner } from "../components/common/Spinner";

const HomeRegular: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const isCool = theme === "cool";

  const { user, loading } = useAuth();

  const [notices, setNotices] = useState<Notice[]>([]);
  const [todaysHighlights, setTodaysHighlights] = useState<CartItem[]>([]);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    // 1. Fetch Notices
    getActiveNotices().then(setNotices);

    // 2. Fetch Today's Daily Menu Highlights
    const fetchHighlights = async () => {
      try {
        const todayId = getLocalDateId(new Date());
        const dailyMenu = await getDailyMenu(todayId);

        if (dailyMenu && dailyMenu.kitchenMenus) {
          const allItems: CartItem[] = [];
          Object.values(dailyMenu.kitchenMenus).forEach((group: any) => {
            if (group.items && Array.isArray(group.items)) {
              allItems.push(...group.items);
            }
          });
          setTodaysHighlights(allItems.slice(0, 5));
        }
      } catch (error) {
        console.error("Failed to load daily highlights", error);
      }
    };
    fetchHighlights();
  }, []);

  const {
    data: kitchens = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery<Kitchen[]>(["allOpenKitchens"], getOpenKitchens, {
    retry: 1,
    // Surface a failure instead of spinning forever if the network hangs
    // (getOpenKitchens also applies its own 15s timeout).
  });

  // Honor hash deep-links (e.g. /#kitchen-grid) beneath the fixed navbar
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [location.hash, isLoading, kitchens.length]);

  return (
    <div
      className="min-h-screen pb-20 bg-cover bg-center bg-fixed bg-no-repeat"
      style={{ backgroundImage: "url('/images/pagebg.png')" }}
    >
      {/* HERO SECTION WITH FLAT LOGO */}
      <div className="pt-4 sm:pt-6 px-4 text-center">
        <div className="flex justify-center mb-2">
          <img
            src="/images/Ruchira-Flat-Logo.png"
            alt="Ruchira Logo"
            className="h-16 md:h-10 object-contain drop-shadow-xl"
          />
        </div>

        <p
          className={`text-base md:text-lg font-semibold max-w-2xl mx-auto mb-6 ${
            isCool ? "text-cyan-400" : "text-orange-600"
          }`}
        >
          Authentic home-cooked meals from your neighborhood chefs.
        </p>

        {/* PROMOTIONAL BANNER */}
        {showBanner && (
          <div className="relative max-w-5xl mx-auto mb-4">
            <button
              onClick={() => setShowBanner(false)}
              className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 group"
              aria-label="Close banner"
            >
              <svg
                className="w-5 h-5 text-gray-600 group-hover:text-gray-800"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src="/images/Ruchira-Banner.png"
              alt="Ruchira Promotional Banner"
              className="w-full rounded-2xl shadow-2xl cursor-pointer hover:shadow-3xl transition-shadow duration-300"
              onClick={() => navigate("/subscription")}
            />
          </div>
        )}
      </div>

      {/* ROLE DASHBOARDS FROM PROFILE ROLES */}
      {!loading && user && user.roles && user.roles.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 mt-2 mb-4">
          <div className="bg-white/90 backdrop-blur rounded-xl shadow-sm border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Your dashboards
              </p>
              <p className="text-[11px] text-gray-400">
                Based on your profile roles
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {user.roles.map((role) => {
                let label = "Dashboard";
                let path = "/";

                // ✅ FIXED: Updated all role paths to match new routing
                if (role === "customer") {
                  label = "My Tiffin";
                  path = "/customer/dashboard";
                } else if (role === "chef") {
                  label = "Chef Panel";
                  path = "/chef/dashboard";
                } else if (role === "admin") {
                  label = "Admin Center";
                  path = "/admin/dashboard";
                } else if (role === "deliveryAgent") {
                  label = "Delivery Panel";
                  path = "/deliveryagent/dashboard"; // ✅ FIXED: Changed from /delivery/DeliveryDashboard
                } else if (role === "kitchenOwner") {
                  label = "Kitchen Manager";
                  path = "/kitchen/dashboard";
                } else if (role === "cashier") {
                  label = "Cashier Panel";
                  path = "/cashier/dashboard";
                }

                return (
                  <button
                    key={role}
                    onClick={() => navigate(path)}
                    className="px-3 py-2 rounded-full border text-xs font-semibold flex items-center gap-1 bg-white hover:bg-orange-50 border-gray-200 text-gray-700"
                  >
                    <span className="uppercase text-[9px] text-gray-400">
                      {role}
                    </span>
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* LEFT COLUMN: MAIN CONTENT */}
          <div className="lg:col-span-3">
            {/* MAIN ACTIONS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* ORDER NOW */}
              <div className="bg-white/90 backdrop-blur rounded-xl shadow-md p-8 flex flex-col items-center text-center border-t-4 border-orange-500 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-3xl mb-4">
                  🍲
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Order A La Carte
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  Hungry now? Browse menus and order fresh dishes instantly.
                </p>
                <button
                  onClick={() =>
                    document
                      .getElementById("kitchen-grid")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="bg-orange-600 text-white px-6 py-2 rounded-full font-bold shadow hover:bg-orange-700 transition-colors w-full"
                >
                  BROWSE KITCHENS
                </button>
              </div>

              {/* SUBSCRIBE */}
              <div className="bg-white/90 backdrop-blur rounded-xl shadow-md p-8 flex flex-col items-center text-center border-t-4 border-blue-500 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-3xl mb-4">
                  📅
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Daily Subscription
                </h2>
                <p className="text-gray-500 mb-6 text-sm">
                  Plan your week. Get breakfast, lunch, and dinner delivered
                  daily.
                </p>
                <button
                  onClick={() => navigate("/subscription")}
                  className="bg-blue-600 text-white px-6 py-2 rounded-full font-bold shadow hover:bg-blue-700 transition-colors w-full"
                >
                  BUILD MEAL PLAN
                </button>
              </div>
            </div>

            {/* KITCHEN GRID */}
            <div id="kitchen-grid" className="scroll-mt-[calc(var(--ruchira-header-height)+1.5rem)]">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-6 bg-white/80 p-4 rounded-xl shadow-sm backdrop-blur-sm">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800">
                  Our Home Kitchens
                </h3>
                <span className="bg-green-100 text-green-800 text-xs font-bold px-3 py-1 rounded-full shrink-0">
                  {kitchens.length} OPEN NOW
                </span>
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <Spinner size={48} />
                  <p className="text-sm text-gray-500">Finding open kitchens…</p>
                </div>
              ) : isError ? (
                <div className="bg-white/95 border border-red-100 rounded-2xl p-8 text-center shadow-sm">
                  <div className="text-3xl mb-2">⚠️</div>
                  <h4 className="font-bold text-gray-900 mb-1">Couldn&apos;t load kitchens</h4>
                  <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                    {(error as Error)?.message ||
                      "Something went wrong while loading nearby kitchens."}
                  </p>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    className="min-h-[44px] px-5 py-2.5 rounded-full bg-orange-600 text-white font-bold shadow hover:bg-orange-700 disabled:opacity-60"
                  >
                    {isFetching ? "Retrying…" : "Try again"}
                  </button>
                </div>
              ) : kitchens.length === 0 ? (
                <div className="bg-white/95 border border-gray-100 rounded-2xl p-8 text-center shadow-sm">
                  <div className="text-3xl mb-2">🏠</div>
                  <h4 className="font-bold text-gray-900 mb-1">No kitchens open right now</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Check back soon, or explore meal plans meanwhile.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/subscription")}
                    className="min-h-[44px] px-5 py-2.5 rounded-full bg-blue-600 text-white font-bold shadow hover:bg-blue-700"
                  >
                    Browse meal plans
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {kitchens.map((kitchen) => (
                    <Card
                      key={kitchen.id}
                      className="cursor-pointer hover:shadow-xl transition-shadow duration-300 overflow-hidden group bg-white/95 backdrop-blur-sm"
                      onClick={() => navigate(`/kitchen/${kitchen.id}`)}
                    >
                      <div className="p-5 flex items-start gap-4">
                        <CircleLogo
                          src={kitchen.logoUrl || "/ruchira-logo.png"}
                          alt={kitchen.kitchen_name}
                          size={56}
                          className="group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="min-w-0">
                          <h4 className="font-bold text-base text-gray-900 leading-tight mb-1">
                            {kitchen.kitchen_name}
                          </h4>
                          <p className="text-xs text-gray-500 mb-2">
                            by {kitchen.owner_name}
                          </p>

                          <div className="flex flex-wrap gap-1">
                            {kitchen.cuisines?.slice(0, 2).map(
                              (tag: string, i: number) => (
                                <span
                                  key={i}
                                  className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                                >
                                  {tag}
                                </span>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="bg-orange-50 px-5 py-2 border-t border-orange-100 flex justify-between items-center">
                        <span className="text-xs font-bold text-orange-600">
                          View Menu
                        </span>
                        <span className="text-orange-600">→</span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: NOTICE BOARD + SPECIALS */}
          <div className="lg:col-span-1">
            <div className="sticky top-[calc(var(--ruchira-header-height)+1.25rem)] space-y-6">
              {/* NOTICE BOARD CARD */}
              <div id="notice-board" className="bg-yellow-50/95 backdrop-blur rounded-xl shadow-sm border border-yellow-200 overflow-hidden scroll-mt-[calc(var(--ruchira-header-height)+1rem)]">
                <div className="bg-yellow-100 px-4 py-3 border-b border-yellow-200 flex items-center gap-2">
                  <span className="text-lg">📌</span>
                  <h3 className="font-bold text-yellow-900 text-sm uppercase tracking-wide">
                    Notice Board
                  </h3>
                </div>

                <div className="divide-y divide-yellow-100/50">
                  {notices.length === 0 ? (
                    <div className="p-4 text-xs text-yellow-800/50 text-center italic">
                      No new announcements today.
                    </div>
                  ) : (
                    notices.map((notice) => (
                      <div
                        key={notice.id}
                        className="p-4 hover:bg-yellow-100/30 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${
                              notice.type === "admin"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {notice.type}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {notice.createdAt?.toDate
                              ? new Date(
                                  notice.createdAt.toDate()
                                ).toLocaleDateString()
                              : "Recent"}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-800 text-sm">
                          {notice.title}
                        </h4>
                        <AnnouncementImages
                          images={(notice as any).images?.length ? (notice as any).images : [(notice as any).photoUrl]}
                          alt={notice.title}
                          className="mt-2"
                        />
                        <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                          {notice.message}
                        </p>
                        {notice.authorName && (
                          <div className="text-[10px] text-gray-400 mt-2 text-right">
                            - {notice.authorName}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* TODAY'S MENU HIGHLIGHTS */}
              <div className="bg-white/95 backdrop-blur rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                  <span className="text-lg">✨</span>
                  <h3 className="font-bold text-gray-700 text-sm uppercase tracking-wide">
                    Today's Specials
                  </h3>
                </div>

                <div className="p-4 space-y-3">
                  {todaysHighlights.length === 0 ? (
                    <div className="text-center py-4 text-xs text-gray-400">
                      <p>No specials set for today.</p>
                      <p className="mt-1">Check back later!</p>
                    </div>
                  ) : (
                    todaysHighlights.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between items-center group cursor-pointer"
                        onClick={() => navigate(`/kitchen/${item.kitchenId}`)}
                      >
                        <div>
                          <div className="font-medium text-sm text-gray-800 group-hover:text-orange-600 transition-colors">
                            {item.name}
                          </div>
                          <div className="flex gap-1 items-center">
                            <span className="text-[10px] text-gray-400">
                              {item.kitchenName}
                            </span>
                            {item.category && (
                              <span className="text-[9px] bg-gray-100 px-1 rounded text-gray-500 capitalize">
                                {item.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="font-bold text-sm text-green-600">
                          ₹{item.price}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <button
                  onClick={() => navigate("/daily-menu")}
                  className="w-full py-2 bg-gray-50 text-xs font-bold text-gray-500 hover:bg-gray-100 transition-colors border-t border-gray-100 min-h-[44px]"
                >
                  VIEW FULL DAILY MENU
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeRegular;

                          
